import React from "react";
import { IconButton } from "@mui/material";
import {
  IoChevronBack,
  IoChevronForward,
  IoPlayBackSharp,
  IoPlayForwardSharp
} from "react-icons/io5";
import VuiBox from "../../VuiBox";
import VuiTypography from "../../VuiTypography";
import { PAGINATION, ACCENT_COLORS } from "utils/designTokens";

const VuiPaginationControls = ({
                                 currentPage,
                                 totalPages,
                                 totalElements,
                                 pageSize,
                                 onPageChange,
                                 onPageSizeChange,
                                 pageSizeOptions = [10, 25, 50, 100],
                                 isLoading = false,
                               }) => {

  // Handle page input change
  const handlePageInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= totalPages) {
      onPageChange(value - 1); // Convert to 0-based index
    }
  };

  return (
    <VuiBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={2}
      pt={4}
      mt={1}
      height="50px" // Fixed height to prevent layout shifts
      sx={{
        borderTop: `1px solid ${ACCENT_COLORS.primary}30`,
        background: "transparent",
      }}
    >
      {/* Left side - Page size selector */}
      <VuiBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
        <VuiBox display="flex" alignItems="center" gap={2}>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Show
          </VuiTypography>
          <VuiBox width="80px">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
              disabled={isLoading}
              style={{
                backgroundColor: PAGINATION.background,
                color: PAGINATION.textColor,
                border: PAGINATION.inputBorder,
                borderRadius: PAGINATION.borderRadius,
                padding: "8px 12px",
                fontSize: "14px",
                outline: "none",
                cursor: isLoading ? "not-allowed" : "pointer",
                width: "100%",
                boxShadow: PAGINATION.boxShadow,
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {pageSizeOptions.map((size) => (
                <option 
                  key={size} 
                  value={size} 
                  style={{ 
                    backgroundColor: "#1a1f37",
                    color: "#fff",
                  }}
                >
                  {size}
                </option>
              ))}
            </select>
          </VuiBox>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            entries
          </VuiTypography>
        </VuiBox>

        <VuiTypography variant="caption" color="text" fontWeight="regular">
          {totalElements > 0 ? (
            <>
              Showing {Math.min(currentPage * pageSize + 1, totalElements)} to{" "}
              {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} entries
            </>
          ) : (
            "No entries to show"
          )}
        </VuiTypography>
      </VuiBox>

      {/* Right side - Page navigation */}
      <VuiBox display="flex" alignItems="center" gap={2}>
        <VuiTypography variant="button" color="text" fontWeight="regular">
          Page
        </VuiTypography>

        {/* First page button */}
        <IconButton
          onClick={() => !isLoading && onPageChange(0)}
          disabled={isLoading || currentPage === 0}
          sx={{
            backgroundColor: PAGINATION.background,
            backdropFilter: PAGINATION.backdropFilter,
            color: PAGINATION.textColor,
            opacity: isLoading || currentPage === 0 ? 0.5 : 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: PAGINATION.backgroundHover,
            },
          }}
          size="small"
        >
          <IoPlayBackSharp size="16px" color={PAGINATION.textColor} />
        </IconButton>

        {/* Previous button */}
        <IconButton
          onClick={() => !isLoading && onPageChange(currentPage - 1)}
          disabled={isLoading || currentPage === 0}
          sx={{
            backgroundColor: PAGINATION.background,
            backdropFilter: PAGINATION.backdropFilter,
            color: PAGINATION.textColor,
            opacity: isLoading || currentPage === 0 ? 0.5 : 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: PAGINATION.backgroundHover,
            },
          }}
          size="small"
        >
          <IoChevronBack size="16px" color={PAGINATION.textColor} />
        </IconButton>

        {/* Page input */}
        <VuiBox width="60px" position="relative">
          <input
            type="number"
            value={currentPage + 1}
            onChange={handlePageInputChange}
            disabled={isLoading}
            min={1}
            max={totalPages || 1}
            style={{
              backgroundColor: PAGINATION.inputBackground,
              backdropFilter: PAGINATION.backdropFilter,
              color: PAGINATION.textColor,
              border: PAGINATION.inputBorder,
              borderRadius: PAGINATION.borderRadius,
              padding: "8px",
              fontSize: "14px",
              width: "100%",
              textAlign: "center",
              outline: "none",
              boxShadow: PAGINATION.boxShadow,
              opacity: isLoading ? 0.7 : 1,
              WebkitAppearance: "none",
              MozAppearance: "textfield",
            }}
          />
          {/* Add a style tag for targeting the spinner controls */}
          <style>
            {`
              /* Hide default spinner controls */
              input[type=number]::-webkit-inner-spin-button,
              input[type=number]::-webkit-outer-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }
              
              /* Firefox */
              input[type=number] {
                -moz-appearance: textfield;
              }
            `}
          </style>
        </VuiBox>

        <VuiTypography variant="button" color="text" fontWeight="regular">
          of {totalPages || 1}
        </VuiTypography>

        {/* Next button */}
        <IconButton
          onClick={() => !isLoading && onPageChange(currentPage + 1)}
          disabled={isLoading || currentPage >= totalPages - 1}
          sx={{
            backgroundColor: PAGINATION.background,
            backdropFilter: PAGINATION.backdropFilter,
            color: PAGINATION.textColor,
            opacity: isLoading || currentPage >= totalPages - 1 ? 0.5 : 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: PAGINATION.backgroundHover,
            },
          }}
          size="small"
        >
          <IoChevronForward size="16px" color={PAGINATION.textColor} />
        </IconButton>

        {/* Last page button */}
        <IconButton
          onClick={() => !isLoading && onPageChange(totalPages - 1)}
          disabled={isLoading || currentPage >= totalPages - 1}
          sx={{
            backgroundColor: PAGINATION.background,
            backdropFilter: PAGINATION.backdropFilter,
            color: PAGINATION.textColor,
            opacity: isLoading || currentPage >= totalPages - 1 ? 0.5 : 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: PAGINATION.backgroundHover,
            },
          }}
          size="small"
        >
          <IoPlayForwardSharp size="16px" color={PAGINATION.textColor} />
        </IconButton>
      </VuiBox>
    </VuiBox>
  );
};

export default VuiPaginationControls;